import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  QueryList,
  Type,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from "@angular/core";
import { MatTabGroup } from "@angular/material/tabs";
import { Tabs } from "../../../core/models/tab-field";
import { MaterialModule } from "../../../core/shared/material.module";

/**
 * This component provides a dynamic tab structure where each tab can load a different component.
 * It supports functionality to change tabs, pass data to dynamically loaded components, and
 * enable or disable tabs programmatically.
 */
@Component({
  selector: "app-tab",
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: "./tab.component.html",
  styleUrl: "./tab.component.scss",
})
export class TabComponent implements AfterViewInit {
  /**
   * Input array defining the tabs with their corresponding configurations and components.
   */
  @Input() tabs: Tabs[] = [];

  @Input() configs: any = {
    isNextArrow:false
  };


  /**
   * Reference to the `MatTabGroup` used in the template for managing tabs.
   */
  @ViewChild("tabGroup") tabGroup!: MatTabGroup;

  /**
   * Query list for dynamic content containers within each tab.
   * These containers are used to load dynamic components.
   */
  @ViewChildren("dynamicContent", { read: ViewContainerRef })
  dynamicContents!: QueryList<ViewContainerRef>;

  selectedTabIndex: number = 0;


  /**
   * Lifecycle hook that triggers after the view initialization.
   * Automatically loads the first tab's component if tabs are defined.
   */
  ngAfterViewInit() {
    if (this.tabs.length > 0) {
      this.loadComponent(0);
    }
  }

  /**
   * Handles the tab change event and loads the component for the selected tab.
   * @param event The tab change event containing the index of the selected tab.
   */
  onTabChange(event: any) {
    const selectedIndex = event.index;
    this.loadComponent(selectedIndex);
    this.selectedTabIndex = selectedIndex;
  
    const resultsTabIndex = this.tabs.findIndex(tab => tab.view === 'results');
    const segmentationTabIndex = this.tabs.findIndex(tab => tab.view === 'segmentation_threshold');

    if (selectedIndex !== resultsTabIndex) {
      if (!this.tabs[resultsTabIndex]?.disabled) {
        this.toggleTabs([resultsTabIndex], false); 
      }
      if (this.tabs[segmentationTabIndex]?.disabled) {
        this.toggleTabs([segmentationTabIndex], true);
      }
    }
  }

  /**
   * Loads a component dynamically into the specified tab index.
   * Passes data and subscribes to events emitted by the dynamically loaded component.
   * @template T The type of the dynamically loaded component.
   * @param index The index of the tab to load the component into.
   */
  loadComponent<T extends ComponentRef<T>>(index: number) {
    const viewContainerRef = this.dynamicContents.toArray()[index];
    viewContainerRef.clear();

    const tab = this.tabs[index];
    if (tab && tab.component) {
      const componentRef = viewContainerRef.createComponent<T>(
        tab.component as Type<T>
      );

      // Pass data to the dynamically loaded component
      (componentRef.instance as T & { data?: any }).data = tab.view;

      // Listen for the event emitted by the dynamically loaded component
      if (
        "moveToNextTabEvent" in componentRef.instance &&
        componentRef.instance.moveToNextTabEvent instanceof EventEmitter
      ) {
        (componentRef.instance as any).moveToNextTabEvent.subscribe(() => {
          this.changeTab(index + 1); // Move to the next tab
        });
      }
      //  Listen for the event emitted Backward navigation
      if (
      "moveToPreviousTabEvent" in componentRef.instance &&
      componentRef.instance.moveToPreviousTabEvent instanceof EventEmitter
      ) {
      (componentRef.instance as any).moveToPreviousTabEvent.subscribe(() => {
      this.changeTab(index - 1);
      });
       }
      // Listen for the enableTabsEvent emitted by the dynamically loaded component
      if (
        "enableTabsEvent" in componentRef.instance &&
        componentRef.instance.enableTabsEvent instanceof EventEmitter
      ) {
        (componentRef.instance as any).enableTabsEvent.subscribe(
          (tabIndices: number[]) => {
            this.toggleTabs(tabIndices, true); // Enable multiple tabs
          }
        );
      }
      if (
        "enableTabsEvent" in componentRef.instance &&
        componentRef.instance.enableTabsEvent instanceof EventEmitter
      ) {
        (componentRef.instance as any).enableTabsEvent.subscribe(
          (tabIndices: number[]) => {
            this.toggleTabs(tabIndices, true);
          }
        );
        // Listen for event to disable specific tabs
        if (
          "disableTabsEvent" in componentRef.instance &&
          componentRef.instance.disableTabsEvent instanceof EventEmitter
        ) {
          (componentRef.instance as any).disableTabsEvent.subscribe(
            (tabIndices: number[]) => {
              this.toggleTabs(tabIndices, false); // ❌ Disable specified tabs
            }
          );
        }
      }   
    }
  }

  /**
   * Changes the active tab programmatically.
   * @param index The index of the tab to activate.
   */
  changeTab(index: number) {
    if (this.tabGroup) {
      this.tabGroup.selectedIndex = index;
    }
  }

  /**
   * Enables or disables tabs based on their indices.
   * @param indices Array of tab indices to enable or disable.
   * @param isEnabled Boolean flag to indicate whether to enable or disable the tabs.
   */
  toggleTabs(indices: number[], isEnabled: boolean): void {
    indices.forEach((index) => {
      if (this.tabs[index]) {
        this.tabs[index].disabled = !isEnabled;
      }
    });
  }
}
